import { S3 } from '~models/s3'
import api from '~utils/http'

class S3RepositoryImpl {
  async getConvertedVideos(fileKeys: string[]) {
    const requestData = fileKeys.map(key => new S3.ConvertedVideo.Request(key))

    const promises = requestData.map(fileInfo =>
      api.post<S3.ConvertedVideo>('/app/aws/convert_video/', fileInfo, { withCredentials: true })
    )

    const results = await Promise.all(promises)
    const convertedVideos = results.map(({ data }) => data)
    return convertedVideos
  }

  async getPreSignedPostUrls(fileType: S3.PreSignedPost.Request[]) {
    const requestData = fileType.map(item => new S3.PreSignedPost.Request(item))

    return (
      await api.post<S3.IPresignMetadata[]>('/app/aws/presigned_post/', requestData, {
        withCredentials: true,
      })
    ).data.map(result => result)
  }

  async uploadPdfToS3(file: File, preSignedPostResponse: S3.PreSignedPost) {
    const { cdn, url, fields } = preSignedPostResponse
    const formData = new FormData()

    Object.entries(fields).forEach(([key, value]) => {
      formData.append(key, value)
    })

    formData.append('file', file)
    await api.post(`${url}`, formData, {
      baseURL: '',
      timeout: 30000,
    })

    return `${cdn}/${fields.key}`
  }

  async uploadMultiplePdfToS3(files: File[], preSignedPostResponse: S3.PreSignedPost[]) {
    const promises = files.map((file, index) =>
      this.uploadPdfToS3(file, preSignedPostResponse[index])
    )
    const results = await Promise.all(promises)

    if (results.length !== files.length) {
      throw new Error('Failed to upload all pdf files')
    }

    return results
  }

  async uploadPhotoToS3(file: File, preSignedPostResponse: S3.PreSignedPost): Promise<string> {
    const { cdn, url, fields } = preSignedPostResponse
    const formData = new FormData()

    Object.entries(fields).forEach(([key, value]) => {
      formData.append(key, value)
    })

    formData.append('file', file)

    await api.post(`${url}`, formData, {
      baseURL: '',
      timeout: 30000,
    })

    const photoUrl = `${cdn}/${fields.key}`

    return photoUrl
  }

  async uploadMultiplePhotoToS3(files: File[], preSignedPostResponse: S3.PreSignedPost[]) {
    const promises = files.map((file, index) =>
      this.uploadPhotoToS3(file, preSignedPostResponse[index])
    )
    const results = await Promise.all(promises)

    if (results.length !== files.length) {
      throw new Error('Failed to upload all photos')
    }

    return results
  }

  async uploadVideoToS3(file: File, preSignedPostResponse: S3.PreSignedPost) {
    const { url, fields } = preSignedPostResponse
    const formData = new FormData()

    Object.entries(fields).forEach(([key, value]) => {
      formData.append(key, value)
    })

    formData.append('file', file)

    await api.post(`${url}`, formData, {
      baseURL: '',
      timeout: 60000,
    })
  }

  async uploadMultipleVideoToS3(files: File[], preSignedPostResponse: S3.PreSignedPost[]) {
    const promises = files.map((file, index) =>
      this.uploadVideoToS3(file, preSignedPostResponse[index])
    )
    const results = await Promise.all(promises)

    if (results.length !== files.length) {
      throw new Error('Failed to upload all videos')
    }

    return results
  }
}

const S3Repository = new S3RepositoryImpl()
export default S3Repository
